import { useState, useEffect } from 'react'

interface CurrencyInfo {
  code: string
  symbol: string
  localPrice: string
  country: string
  flag: string
  loading: boolean
}

const CURRENCY_MAP: Record<
  string,
  {
    code: string
    symbol: string
    rate: number
    flag: string
    format: (n: number) => string
  }
> = {
  CR: {
    code: 'CRC',
    symbol: '₡',
    rate: 512,
    flag: '🇨🇷',
    format: (n) => n.toLocaleString('es-CR', { maximumFractionDigits: 0 }),
  },
  MX: {
    code: 'MXN',
    symbol: '$',
    rate: 17.2,
    flag: '🇲🇽',
    format: (n) => n.toLocaleString('es-MX', { maximumFractionDigits: 0 }),
  },
  CO: {
    code: 'COP',
    symbol: '$',
    rate: 4160,
    flag: '🇨🇴',
    format: (n) => n.toLocaleString('es-CO', { maximumFractionDigits: 0 }),
  },
  AR: {
    code: 'ARS',
    symbol: '$',
    rate: 1015,
    flag: '🇦🇷',
    format: (n) => n.toLocaleString('es-AR', { maximumFractionDigits: 0 }),
  },
  CL: {
    code: 'CLP',
    symbol: '$',
    rate: 965,
    flag: '🇨🇱',
    format: (n) => n.toLocaleString('es-CL', { maximumFractionDigits: 0 }),
  },
  PE: {
    code: 'PEN',
    symbol: 'S/',
    rate: 3.75,
    flag: '🇵🇪',
    format: (n) => n.toLocaleString('es-PE', { maximumFractionDigits: 0 }),
  },
  GT: {
    code: 'GTQ',
    symbol: 'Q',
    rate: 7.75,
    flag: '🇬🇹',
    format: (n) => n.toLocaleString('es-GT', { maximumFractionDigits: 0 }),
  },
  HN: {
    code: 'HNL',
    symbol: 'L',
    rate: 24.8,
    flag: '🇭🇳',
    format: (n) => n.toLocaleString('es-HN', { maximumFractionDigits: 0 }),
  },
  NI: {
    code: 'NIO',
    symbol: 'C$',
    rate: 36.7,
    flag: '🇳🇮',
    format: (n) => n.toLocaleString('es-NI', { maximumFractionDigits: 0 }),
  },
  DO: {
    code: 'DOP',
    symbol: 'RD$',
    rate: 58.5,
    flag: '🇩🇴',
    format: (n) => n.toLocaleString('es-DO', { maximumFractionDigits: 0 }),
  },
  UY: {
    code: 'UYU',
    symbol: '$U',
    rate: 42.5,
    flag: '🇺🇾',
    format: (n) => n.toLocaleString('es-UY', { maximumFractionDigits: 0 }),
  },
  BO: {
    code: 'BOB',
    symbol: 'Bs',
    rate: 6.91,
    flag: '🇧🇴',
    format: (n) => n.toLocaleString('es-BO', { maximumFractionDigits: 0 }),
  },
  PY: {
    code: 'PYG',
    symbol: '₲',
    rate: 7500,
    flag: '🇵🇾',
    format: (n) => n.toLocaleString('es-PY', { maximumFractionDigits: 0 }),
  },
  VE: {
    code: 'VES',
    symbol: 'Bs',
    rate: 36.5,
    flag: '🇻🇪',
    format: (n) => n.toLocaleString('es-VE', { maximumFractionDigits: 0 }),
  },
  BR: {
    code: 'BRL',
    symbol: 'R$',
    rate: 5.0,
    flag: '🇧🇷',
    format: (n) => n.toLocaleString('pt-BR', { maximumFractionDigits: 0 }),
  },
  ES: {
    code: 'EUR',
    symbol: '€',
    rate: 0.92,
    flag: '🇪🇸',
    format: (n) => n.toLocaleString('es-ES', { maximumFractionDigits: 0 }),
  },
  // USD countries
  US: {
    code: 'USD',
    symbol: '$',
    rate: 1,
    flag: '🇺🇸',
    format: (n) => n.toLocaleString('en-US', { maximumFractionDigits: 0 }),
  },
  EC: {
    code: 'USD',
    symbol: '$',
    rate: 1,
    flag: '🇪🇨',
    format: (n) => n.toLocaleString('es-EC', { maximumFractionDigits: 0 }),
  },
  PA: {
    code: 'USD',
    symbol: '$',
    rate: 1,
    flag: '🇵🇦',
    format: (n) => n.toLocaleString('es-PA', { maximumFractionDigits: 0 }),
  },
  SV: {
    code: 'USD',
    symbol: '$',
    rate: 1,
    flag: '🇸🇻',
    format: (n) => n.toLocaleString('es-SV', { maximumFractionDigits: 0 }),
  },
}

const BASE_PRICE_USD = 197

export function useLocalCurrency(): CurrencyInfo {
  const [info, setInfo] = useState<CurrencyInfo>({
    code: 'USD',
    symbol: '$',
    localPrice: '197',
    country: '',
    flag: '',
    loading: true,
  })

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const res = await fetch('https://ipapi.co/json/')
        const data = await res.json()
        const countryCode = data.country_code as string

        const currency = CURRENCY_MAP[countryCode]

        if (currency && currency.code !== 'USD') {
          const converted = Math.round(BASE_PRICE_USD * currency.rate)
          setInfo({
            code: currency.code,
            symbol: currency.symbol,
            localPrice: currency.format(converted),
            country: data.country_name || '',
            flag: currency.flag,
            loading: false,
          })
        } else {
          setInfo({
            code: 'USD',
            symbol: '$',
            localPrice: '197',
            country: data.country_name || '',
            flag: currency?.flag || '',
            loading: false,
          })
        }
      } catch {
        setInfo((prev) => ({ ...prev, loading: false }))
      }
    }

    fetchLocation()
  }, [])

  return info
}
